import { SearchOutlined } from "@ant-design/icons";
import NiceModal from "@ebay/nice-modal-react";
import {
  Card,
  Col,
  Form,
  Input,
  Progress,
  Row,
  Space,
  TableColumnsType,
  Tag,
  Typography,
} from "antd";
import dayjs from "dayjs";
import * as React from "react";
import { useRecoilValue } from "recoil";
import { Button, ProgressLogModal, QuestDebugModal, QuestStatusModal, Select, Table } from "src/components";
import {
  getQuestProgressStatusLabel,
  getQuestTypeLabel,
  getRewardTypeLabel,
  QuestProgressItem,
  QuestProgressStatus,
  useGetAllQuests,
  useGetQuestProgress,
  useListAffiliateCampaigns,
} from "src/hooks/affiliate";
import { authInfoState } from "src/recoils";
import { Scope } from "src/types";
import { datetime } from "src/utils";

const { Title, Text } = Typography;

const formatDateTime = (value?: string | null) =>
  value ? datetime(value).format("DD/MM/YYYY HH:mm") : "-";

const statusColor: Record<string, string> = {
  [QuestProgressStatus.IN_PROGRESS]: "blue",
  [QuestProgressStatus.COMPLETED]: "green",
  [QuestProgressStatus.REWARDED]: "gold",
};

const progressColor = (status: QuestProgressStatus) => {
  switch (status) {
    case QuestProgressStatus.COMPLETED:
      return "#52c41a";
    case QuestProgressStatus.REWARDED:
      return "#faad14";
    default:
      return undefined;
  }
};

interface FilterFormValues {
  playerCodeOrPhone?: string;
  campaignCode?: string;
  questUid?: string;
  progressStatus?: QuestProgressStatus;
}

const statusOptions = [
  { value: QuestProgressStatus.IN_PROGRESS, label: getQuestProgressStatusLabel(QuestProgressStatus.IN_PROGRESS) },
  { value: QuestProgressStatus.COMPLETED, label: getQuestProgressStatusLabel(QuestProgressStatus.COMPLETED) },
  { value: QuestProgressStatus.REWARDED, label: getQuestProgressStatusLabel(QuestProgressStatus.REWARDED) },
];

const AffiliateQuestProgress: React.FunctionComponent = () => {
  const { scope } = useRecoilValue(authInfoState);
  const canViewQuestDetail = scope[Scope.AffiliateQuestDetailButton]?.enable;
  const [filters, setFilters] = React.useState<FilterFormValues>({});
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(20);

  const { data: campaignList, isLoading: isCampaignsLoading } = useListAffiliateCampaigns();
  const campaignOptions = React.useMemo(
    () =>
      (campaignList?.campaigns || []).map((c) => ({
        value: c.campaignCode,
        label: `${c.campaignCode} — ${c.campaignName}`,
      })),
    [campaignList],
  );

  const { data: allQuests, isLoading: isQuestsLoading } = useGetAllQuests();
  const questOptions = React.useMemo(
    () =>
      (allQuests?.quests || []).map((q) => ({
        value: q.uid,
        label: q.campaignCode ? `${q.questName} (${q.campaignCode})` : q.questName,
      })),
    [allQuests],
  );

  const { data, isFetching, refetch } = useGetQuestProgress({
    playerCodeOrPhone: filters.playerCodeOrPhone || "",
    campaignCode: filters.campaignCode,
    questUid: filters.questUid,
    progressStatus: filters.progressStatus,
    page,
    pageSize,
  });

  const handleSearch = (values: FilterFormValues) => {
    setFilters({
      playerCodeOrPhone: values.playerCodeOrPhone?.trim(),
      campaignCode: values.campaignCode?.trim() || undefined,
      questUid: values.questUid?.trim() || undefined,
      progressStatus: values.progressStatus,
    });
    setPage(1);
    // บังคับ refetch เผื่อกดค้นหาด้วยค่าฟิลเตอร์เดิม (query key ไม่เปลี่ยน → react-query
    // จะคืน cache เดิมและไม่ยิง request ใหม่). ดึงข้อมูลล่าสุดเสมอเมื่อผู้ใช้กดค้นหา
    refetch();
  };

  const openLogModal = (record: QuestProgressItem) => {
    NiceModal.show(ProgressLogModal, { progress: record });
  };

  const openDebugModal = (record: QuestProgressItem) => {
    NiceModal.show(QuestDebugModal, { progress: record });
  };

  const openStatusModal = (record: QuestProgressItem) => {
    NiceModal.show(QuestStatusModal, { progress: record });
  };

  const columns: TableColumnsType<QuestProgressItem> = [
    {
      title: "แคมเปญ",
      key: "campaign",
      width: 200,
      render: (_, record) => (
        <>
          <Tag color="blue">{record.campaignCode}</Tag>
          <div style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }}>
            {record.campaignName}
          </div>
        </>
      ),
    },
    {
      title: "เควส",
      key: "quest",
      render: (_, record) => (
        <>
          <div>{record.questName}</div>
          <Tag>{getQuestTypeLabel(record.questType)}</Tag>
          <Tag color="gold">{getRewardTypeLabel(record.rewardType)}</Tag>
        </>
      ),
    },
    {
      title: "ความคืบหน้า",
      key: "progress",
      width: 240,
      render: (_, record) => (
        <div>
          <Progress
            percent={record.percent}
            size="small"
            strokeColor={progressColor(record.status)}
            format={() => `${record.currentValue}/${record.targetValue}`}
          />
        </div>
      ),
    },
    {
      title: "สถานะ",
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (v: QuestProgressStatus) => (
        <Tag color={statusColor[v] || "default"}>{getQuestProgressStatusLabel(v)}</Tag>
      ),
      filters: statusOptions.map((s) => ({ text: s.label, value: s.value })),
      onFilter: (value, record) => record.status === value,
    },
    {
      title: "วันที่สำเร็จ",
      dataIndex: "completedAt",
      key: "completedAt",
      width: 150,
      render: formatDateTime,
    },
    {
      title: "วันที่รับรางวัล",
      dataIndex: "rewardedAt",
      key: "rewardedAt",
      width: 150,
      render: formatDateTime,
    },
    {
      title: "อัปเดตล่าสุด",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 150,
      render: formatDateTime,
      sorter: (a, b) =>
        dayjs(a.updatedAt || 0).diff(dayjs(b.updatedAt || 0)),
    },
    {
      title: "",
      key: "action",
      width: 340,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          <Button size="small" onClick={() => openLogModal(record)}>
            ดูประวัติ
          </Button>
          <Button size="small" onClick={() => openStatusModal(record)}>
            เช็คสถานะ
          </Button>
          {canViewQuestDetail && (
            <Button size="small" type="primary" ghost onClick={() => openDebugModal(record)}>
              รายละเอียดเควส
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Title level={3}>เช็คความคืบหน้าเควสรายลูกค้า</Title>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Form layout="vertical" onFinish={handleSearch} autoComplete="off">
          <Row gutter={[16, 0]} align="bottom">
            <Col span={6}>
              <Form.Item
                label="รหัส หรือ เบอร์โทร"
                name="playerCodeOrPhone"
                rules={[{ required: true, message: "ระบุรหัส หรือเบอร์โทร" }]}
              >
                <Input placeholder="PS123456789 หรือ 0812345678" />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item label="แคมเปญ" name="campaignCode">
                <Select
                  width="100%"
                  allowClear
                  showSearch
                  loading={isCampaignsLoading}
                  placeholder="ทุกแคมเปญ (ไม่บังคับ)"
                  options={campaignOptions}
                  optionFilterProp="label"
                  notFoundContent={isCampaignsLoading ? "กำลังโหลด..." : "ไม่พบแคมเปญ"}
                />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item label="เควส" name="questUid">
                <Select
                  width="100%"
                  allowClear
                  showSearch
                  loading={isQuestsLoading}
                  placeholder="ทุกเควส (ไม่บังคับ)"
                  options={questOptions}
                  optionFilterProp="label"
                  notFoundContent={isQuestsLoading ? "กำลังโหลด..." : "ไม่พบเควส"}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="สถานะ" name="progressStatus">
                <Select
                  allowClear
                  options={statusOptions}
                  placeholder="ทุกสถานะ"
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label=" ">
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SearchOutlined />}
                  loading={isFetching}
                  style={{ width: "100%" }}
                >
                  ค้นหา
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>

      {!filters.playerCodeOrPhone && (
        <Card>
          <Text type="secondary">
            กรอกรหัส หรือเบอร์โทรลูกค้าด้านบน แล้วกดค้นหา
          </Text>
        </Card>
      )}

      {filters.playerCodeOrPhone && (
        <Card
          size="small"
          title={`ผลลัพธ์ (${data?.total ?? 0} รายการ)`}
        >
          <Table
            rowKey="uid"
            loading={isFetching}
            columns={columns}
            dataSource={data?.items || []}
            pagination={{
              current: page,
              pageSize,
              total: data?.total ?? 0,
              showSizeChanger: true,
              onChange: (p, ps) => {
                setPage(p);
                setPageSize(ps);
              },
            }}
            scroll={{ x: 1600 }}
          />
        </Card>
      )}
    </div>
  );
};

export default AffiliateQuestProgress;
